class MemysMultibanner extends HTMLElement {
  async connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    await customElements.whenDefined('slideshow-component');
    if (!this.isConnected || signal.aborted) return;
    this.slider = this.querySelector('slideshow-component');
    if (!this.slider) return;
    this.slides = [...this.querySelectorAll('[data-memys-slide]')];
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.isVisible = true;
    this.userPaused = this.dataset.autoplay !== 'true' || this.hasAttribute('data-design-mode') || this.motion.matches;
    this.firstReady = false;
    this.busy = false;
    this.nativeSelect = this.slider.select.bind(this.slider);
    this.nativePlay = this.slider.play.bind(this.slider);
    this.nativePause = this.slider.pause.bind(this.slider);
    this.nativeResume = this.slider.resume.bind(this.slider);
    this.slider.select = (input, event, options) => this.select(input, event, options);
    this.slider.play = () => {
      this.userPaused = false;
      this.slider.paused = false;
      this.resume();
    };
    this.slider.pause = () => {
      this.userPaused = true;
      this.nativePause();
      this.slider.setAttribute('aria-live', 'polite');
    };
    this.slider.resume = () => this.resume();
    this.nativePause();

    this.addEventListener('click', (event) => {
      const control = event.target.closest('button, a');
      if (!control || control.classList.contains('memys-mb-rotation')) return;
      this.pause();
    }, { capture: true, signal });
    this.addEventListener('focusin', (event) => {
      if (!event.target.closest('.memys-mb-rotation')) this.pause();
    }, { signal });
    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' && !event.target.closest('.memys-mb-rotation')) this.pause();
    }, { signal });
    this.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0 || !event.target.closest('.memys-mb-media')) return;
      this.pause();
      this.gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
      this.setPointerCapture(event.pointerId);
      if (event.pointerType === 'mouse') event.preventDefault();
    }, { signal });
    this.addEventListener('mousedown', (event) => {
      if (!event.target.closest('.memys-mb-media')) return;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal });
    this.addEventListener('pointermove', (event) => {
      if (!this.gesture || event.pointerId !== this.gesture.id) return;
      if (Math.abs(event.clientX - this.gesture.x) > Math.abs(event.clientY - this.gesture.y)) event.preventDefault();
    }, { signal });
    this.addEventListener('pointerup', (event) => {
      const gesture = this.gesture;
      this.gesture = undefined;
      if (!gesture || event.pointerId !== gesture.id) return;
      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) this.select(this.slider.current + (dx < 0 ? 1 : -1), event);
    }, { signal });
    this.addEventListener('pointercancel', () => { this.gesture = undefined; }, { signal });
    this.addEventListener('keydown', (event) => {
      const direction = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
      if (!direction) return;
      event.preventDefault();
      this.pause();
      this.select(this.slider.current + direction, event);
    }, { signal });
    this.addEventListener('slideshow:select', (event) => {
      if (event.target !== this.slider) return;
      this.updateSlides(event.detail.index);
      if (event.detail.userInitiated) this.pause();
      this.preloadNext(event.detail.index);
    }, { signal });
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.pause();
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.slider.suspend();
      else this.resume();
    }, { signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      this.pause();
      const slide = event.target.closest('[data-memys-slide]');
      if (slide) this.select(Number(slide.dataset.memysSlide), event, { animate: false });
    }, { signal });
    this.observer = new IntersectionObserver(([entry]) => {
      this.isVisible = entry.isIntersecting;
      if (!this.isVisible) this.slider.suspend();
      else this.resume();
    }, { threshold: 0.05 });
    this.observer.observe(this);
    this.updateSlides();
    this.firstReady = await this.readyImage(this.slides[0]);
    if (!this.isConnected || signal.aborted) return;
    this.preloadNext();
    this.resume();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    if (this.slider) {
      this.nativePause?.();
      this.slider.select = this.nativeSelect;
      this.slider.play = this.nativePlay;
      this.slider.pause = this.nativePause;
      this.slider.resume = this.nativeResume;
    }
  }

  pause() {
    this.userPaused = true;
    this.nativePause();
    this.slider.setAttribute('aria-live', 'polite');
  }

  resume() {
    if (!this.firstReady || this.userPaused || !this.isVisible || document.hidden || this.slider.matches(':hover')) {
      this.slider.suspend();
      return;
    }
    this.slider.setAttribute('aria-live', 'off');
    this.nativePlay(Number(this.dataset.interval || 4) * 1000);
  }

  hydrateImage(slide) {
    const picture = slide?.querySelector('picture[data-memys-deferred]');
    if (!picture) return;
    for (const node of picture.querySelectorAll('source, img')) {
      for (const attribute of ['srcset', 'src']) {
        const value = node.getAttribute(`data-${attribute}`);
        if (value) {
          node.setAttribute(attribute, value);
          node.removeAttribute(`data-${attribute}`);
        }
      }
    }
    picture.removeAttribute('data-memys-deferred');
  }

  async readyImage(slide) {
    this.hydrateImage(slide);
    const image = slide?.querySelector('img');
    if (!image) return false;
    image.loading = 'eager';
    if (!image.complete) {
      const loaded = await new Promise((resolve) => {
        const complete = () => { image.removeEventListener('load', success); image.removeEventListener('error', failure); };
        const success = () => { complete(); resolve(true); };
        const failure = () => { complete(); resolve(false); };
        image.addEventListener('load', success, { once: true });
        image.addEventListener('error', failure, { once: true });
      });
      if (!loaded) return false;
    }
    if (!image.naturalWidth) return false;
    try {
      await image.decode();
      return true;
    } catch {
      return image.complete && image.naturalWidth > 0;
    }
  }

  preloadNext(current = this.slider.current) {
    const next = (current + 1) % this.slides.length;
    this.hydrateImage(this.slides[next]);
    const image = this.slides[next]?.querySelector('img');
    if (image) image.loading = 'eager';
  }

  updateSlides(current = this.slider.current) {
    this.slides.forEach((slide, index) => {
      const hidden = index !== current;
      if (slide.inert !== hidden) slide.inert = hidden;
    });
  }

  async select(input, event, options = {}) {
    if (!this.isConnected) return;
    if (this.busy) {
      this.pending = { input, event, options };
      return;
    }
    let index;
    if (typeof input === 'object') index = this.slides.findIndex((slide) => slide.getAttribute('slide-id') === input.id);
    else index = Number(input);
    if (!Number.isInteger(index)) return;
    index = (index + this.slides.length) % this.slides.length;
    if (index === this.slider.current) return;
    if (event) this.pause();
    this.busy = true;
    try {
      if (!await this.readyImage(this.slides[index])) {
        this.pause();
        return;
      }
      if (!this.isConnected || this.controller.signal.aborted) return;
      const outgoing = this.slides[this.slider.current];
      const incoming = this.slides[index];
      const animated = !this.motion.matches && options.animate !== false;
      if (animated) await outgoing.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, fill: 'forwards' }).finished;
      await this.nativeSelect(index, event, { animate: false });
      outgoing.getAnimations().forEach((animation) => animation.cancel());
      if (animated) await incoming.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150 }).finished;
      this.updateSlides();
      this.preloadNext();
    } finally {
      this.busy = false;
      const pending = this.pending;
      this.pending = undefined;
      if (pending) this.select(pending.input, pending.event, pending.options);
    }
  }
}

if (!customElements.get('memys-multibanner')) customElements.define('memys-multibanner', MemysMultibanner);
